import { css } from '@emotion/react';
import { observer } from 'mobx-react-lite';
import { ReactElement, ReactNode, useRef } from 'react';
import { createContext, useEffect, useMemo } from 'react';

import { useSDK } from '../../app';
import { useUIState } from '../../app/App';
import { styled } from '../../styled';
import type { Theme } from '../../theme/interface/detailed';
import { useTheme } from '../../theme/useTheme';
import { isMobile } from '../../utils/isMobile';
import { useDrag, UseDragReturnType } from '../../utils/useDrag';

type Interface = { children: ReactNode; className: string };

export const DragCtx = createContext<Omit<UseDragReturnType, 'position' | 'setPosition'>>({} as UseDragReturnType);

export const calcBottom = (theme: Theme): number =>
	parseInt(theme.allSettings.containers.appContainer.bottom as string) +
	parseInt(theme.allSettings.icons.openWidgetIcon.height as string) +
	parseInt(theme.allSettings.containers.appContainer.openBtnAndWidgetMargin as string) +
	27; // open widget button padding

const Component = ({ children, className }: Interface): ReactElement => {
	const { uiStateStore } = useSDK();
	const theme = useTheme();
	const {
		clientViewport: { width },
	} = useUIState();
	const bottom = calcBottom(theme);
	const containerRef = useRef<HTMLDivElement>(null);
	const startPos = useMemo(
		() =>
			uiStateStore.chatPos || {
				right: parseInt(theme.allSettings.containers.appContainer.right as string),
				bottom,
			}, // eslint-disable-next-line
		[
			uiStateStore.chatPos,
			theme.allSettings.containers.appContainer.right,
			theme.allSettings.containers.appContainer.bottom,
		]
	);

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const { position, setPosition, ...listeners } = useDrag(startPos, containerRef.current, {
		bottom: parseInt(theme.allSettings.containers.appContainer.bottom as string) + 60,
		right: parseInt(theme.allSettings.containers.appContainer.right as string),
	});

	useEffect(() => {
		uiStateStore.setChatPos(position);
	}, [position, uiStateStore]);
	const style = useMemo(
		() =>
			isMobile
				? { right: 0, bottom: 0 }
				: uiStateStore.chatPos
				? {
						right: uiStateStore.chatPos.right,
						bottom: uiStateStore.chatPos.bottom,
				  }
				: { right: startPos.right, bottom: startPos.bottom }, // eslint-disable-next-line
		[uiStateStore.chatPos, startPos, uiStateStore.chatPos?.right, uiStateStore.chatPos?.bottom, width]
	);
	return (
		<DragCtx.Provider value={listeners}>
			<div ref={containerRef} className={className} style={style}>
				{children}
			</div>
		</DragCtx.Provider>
	);
};

export const DraggableArea = styled(observer(Component))(
	css`
		display: flex;
		position: fixed;
		z-index: 100050;
	`
);
